// src/utils/api/auth-bridge.js
// Bridge between AuthContext and APIs:
// - Token function management (registration and usage)
// - User information retrieval from tokens
// - Tenant and API key extraction

import {
  extractTenantsAndApiKeys,
  setUserTenants,
  getUserTenants,
  determineAndSetTenant,
  getSelectedTenantApiKey
} from "../roles";
import { logger } from "../logger";

const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || "").toLowerCase();
const isClientCredentialsMode = AUTH_MODE === "client_credentials";

// True if production environment (run with npm run build)(dev, test, imp, prod)
// or if REACT_APP_FORCE_OAUTH=true.
// Set REACT_APP_DISABLE_OAUTH=true to opt out (for wrappers that do not use MSAL,
// e.g. clients relying on API key only or on platform-level auth like Easy Auth).
const shouldUseOAuth =
  process.env.REACT_APP_DISABLE_OAUTH !== 'true' &&
  (isClientCredentialsMode || process.env.NODE_ENV === 'production' || process.env.REACT_APP_FORCE_OAUTH === 'true');

// Feature flag to include Bearer token in API requests (defaults to enabled).
// Set REACT_APP_ENABLE_BEARER_TOKEN=false to send only X-Api-Key.
const shouldIncludeBearerToken = process.env.REACT_APP_ENABLE_BEARER_TOKEN !== "false";

let getAuthTokenFunction = null;
let tokenRefreshFunction = null;
let logoutFunction = null;

// Deduplicates concurrent getAuthHeadersData() calls from multiple axios interceptors
let pendingAuthHeaders = null;

// Flag to prevent logout during initialization (race condition guard)
let appInitialized = false;

/**
 * Mark the app as fully initialized (auth + API config done)
 * Prevents premature logout on 401s during startup
 */
export function setAppInitialized(value) {
  logger.debug("[API] App initialized:", value);
  appInitialized = value;
}

/**
 * Check if app is fully initialized
 */
export function isAppInitialized() {
  return appInitialized;
}

const TOKEN_EXPIRATION_KEY = "auth_token_exp_ms";
// 0 disables proactive refresh checks
const TOKEN_REFRESH_THRESHOLD_MS = 0;


export const registerAuthTokenFunction = (getTokenFn) => {
  logger.debug("[API] Registering auth token function:", typeof getTokenFn);
  getAuthTokenFunction = getTokenFn;
};

export const registerTokenRefreshFunction = (refreshFn) => {
  logger.debug("[API] Registering token refresh function:", typeof refreshFn);
  tokenRefreshFunction = refreshFn;
};

export const registerLogoutFunction = (logoutFn) => {
  logoutFunction = logoutFn;
};

export function setTokenExpirationFromClaims(claims) {
  if (!shouldIncludeBearerToken) {
    return;
  }
  const expSeconds = claims?.exp;
  if (typeof expSeconds === "number") {
    sessionStorage.setItem(TOKEN_EXPIRATION_KEY, String(expSeconds * 1000));
  }
}

export function getTokenExpirationMs() {
  const raw = sessionStorage.getItem(TOKEN_EXPIRATION_KEY);
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isTokenExpiringSoon() {
  if (!shouldIncludeBearerToken || TOKEN_REFRESH_THRESHOLD_MS <= 0) {
    return false;
  }
  const expMs = getTokenExpirationMs();
  if (!expMs) {
    return false;
  }
  return expMs - Date.now() <= TOKEN_REFRESH_THRESHOLD_MS;
}

/**
 * Triggers logout - called when auth fails for non-recoverable reasons
 */
export function triggerLogout() {
  if (logoutFunction) {
    logger.info("[API] Triggering logout due to auth failure");
    logoutFunction();
  } else {
    logger.warn("[API] Logout function not registered");
  }
}

/**
 * Gets user information from token claims
 * Extracts tenants, API keys, and roles
 * @returns {Promise<Object>} tenantApiKeyMap - Map of tenant to API key
 */
export async function getUserInfo() {
  if (isClientCredentialsMode) {
    logger.debug("[API] Client credentials mode detected, using env tenant/API key");
    const tenant = process.env.REACT_APP_CLIENT_CREDENTIALS_TENANT;
    const apiKeys = {[tenant]: process.env.REACT_APP_API_KEY};

    setUserTenants([tenant]);
    sessionStorage.setItem("tenantApiKeyMap", JSON.stringify(apiKeys));
    sessionStorage.setItem("tenantRoleMap", JSON.stringify({ [tenant]: "user" }));

    const currentTenant = sessionStorage.getItem("userTenant");
    if (!currentTenant || currentTenant === "null" || currentTenant === "undefined") {
      sessionStorage.setItem("userTenant", JSON.stringify(tenant));
    }

    return apiKeys;
  }

  if (!shouldUseOAuth) {
    logger.debug("[API] Local environment detected, using local tenants");
    const apiKeys = {
      dev: process.env.REACT_APP_API_KEY,
      test: process.env.REACT_APP_API_KEY,
      prod: process.env.REACT_APP_API_KEY,
    };

    // Set a default tenant for local environment
    const tenants = ["dev", "test", "prod"];
    setUserTenants(tenants);

    // Set default tenant if not already set
    const currentTenant = sessionStorage.getItem("userTenant");
    if (!currentTenant || currentTenant === "null" || currentTenant === "undefined") {
      sessionStorage.setItem("userTenant", JSON.stringify("dev"));
    }

    return apiKeys;
  }

  try {
    // Get token from AuthProvider
    if (!getAuthTokenFunction) {
      logger.warn("[API] ⚠️ getAuthTokenFunction is not registered yet!");
      return {};
    }

    let tokenResponse = await getAuthTokenFunction();

    if (!tokenResponse) {
      logger.error("[API] No token response received");
      return {};
    }

    // Extract tokens and claims from response object
    const { idToken, idTokenClaims } = tokenResponse;
    // Always prefer idTokenClaims that are already validated by MSAL
    const claims = idTokenClaims || null;

    if (!claims) {
      logger.error("[API] No ID token claims available");
      return {};
    }

    logger.debugToken("[API] ID Token", idToken);

    const { tenantApiKeyMap, tenantRoleMap, tenants } = extractTenantsAndApiKeys(claims);
    logger.debug("[API] Extracted tenants:", tenants);
    logger.debug("[API] Tenant API Key Map:", tenantApiKeyMap);
    logger.debug("[API] Tenant Role Map:", tenantRoleMap);

    // Only update stored data when extraction succeeds (avoids clearing on token refresh)
    if (tenants.length) {
      setUserTenants(tenants);
      sessionStorage.setItem("tenantApiKeyMap", JSON.stringify(tenantApiKeyMap));
    }
    const currentTenant = determineAndSetTenant(tenants.length ? tenants : getUserTenants());
    logger.debug("[API] Current selected tenant:", currentTenant);

    // DEBUG: Verificar autenticación completa
    logger.debug('🔑 Auth Debug:', {
      tenants,
      hasApiKeys: Object.keys(tenantApiKeyMap).length > 0,
      apiKeysSample: Object.keys(tenantApiKeyMap).map(t => `${t}: ${tenantApiKeyMap[t] ? '***' + tenantApiKeyMap[t].slice(-4) : 'null'}`),
      selectedTenant: currentTenant,
      selectedApiKey: tenantApiKeyMap[currentTenant] ? '***' + tenantApiKeyMap[currentTenant].slice(-4) : 'NOT FOUND',
      roles: tenantRoleMap
    });

    // If extraction returned empty, use cached map from a previous successful call
    if (Object.keys(tenantApiKeyMap).length === 0) {
      try {
        const cached = sessionStorage.getItem("tenantApiKeyMap");
        if (cached) return JSON.parse(cached);
      } catch { /* ignore */ }
    }
    return tenantApiKeyMap;
  } catch (error) {
    logger.error("[API] Error processing token claims:", error);
    return {};
  }
}


/**
 * Gets the API key and auth token for request headers
 * Used by axios interceptors
 * Concurrent calls share the same in-flight promise to avoid redundant MSAL calls
 * @returns {Promise<{apiKey: string|null, accessToken: string|null}>}
 */
export async function getAuthHeadersData() {
  if (!shouldUseOAuth) {
    logger.debug("[API] Local environment detected, using env API key");
    return {
      apiKey: process.env.REACT_APP_API_KEY || null,
      accessToken: null  // No OAuth token in local env
    };
  }

  if (pendingAuthHeaders) {
    return pendingAuthHeaders;
  }

  pendingAuthHeaders = (async () => {
    try {
      const apiConfig = sessionStorage.getItem("apiConfig");
      let apiKey = null;

      if (apiConfig) {
        const config = JSON.parse(apiConfig);
        apiKey = config.apiKey;
      } else {
        // Fallback to getting API key from getUserInfo
        const tenantApiKeyMap = await getUserInfo();
        apiKey = getSelectedTenantApiKey(tenantApiKeyMap);
      }
      apiKey = apiKey || process.env.REACT_APP_API_KEY || null;

      // Get Bearer token (use access token only) when feature flag is enabled
      let accessToken = null;
      if (shouldIncludeBearerToken && getAuthTokenFunction) {
        const shouldRefresh = isTokenExpiringSoon();
        const tokenResponse = await getAuthTokenFunction(
          shouldRefresh ? { forceRefresh: true, silentOnly: true } : undefined
        );
        if (tokenResponse?.accessToken) {
          accessToken = tokenResponse.accessToken;
        }
      }

      // DEBUG: Verificar headers que se enviarán
      logger.debug('📡 Headers Debug:', {
        hasApiKey: !!apiKey,
        apiKeySample: apiKey ? '***' + apiKey.slice(-4) : 'NULL',
        willSendBearerToken: shouldIncludeBearerToken,
        hasBearerToken: !!accessToken,
        bearerTokenSample: accessToken ? accessToken.substring(0, 20) + '...' : 'NULL'
      });

      return { apiKey, accessToken };
    } catch (e) {
      logger.error("[API] Error getting auth headers data:", e);
      return { apiKey: null, accessToken: null };
    }
  })().finally(() => {
    pendingAuthHeaders = null;
  });

  return pendingAuthHeaders;
}


export function getTokenRefreshFunction() {
  return tokenRefreshFunction;
}
